//trial
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { getSubscriptionPlans, createCheckoutSession } from "../api/subscription";
import { isGuestUser, getGuestExamUser } from "../utils/storage";
import { GoGraph } from "react-icons/go";
import { FaLightbulb } from "react-icons/fa";
import { MdNoteAlt } from "react-icons/md";
import { FaRocket } from "react-icons/fa";
import "./Pricing.css";
import { CheckIcon, StarIcon, ArrowIcon, InfoIcon } from "@/assets/icons";


interface Price {
  id: string;
  currency: string;
  unit_amount: number;
  formatted_amount: string;
  recurring: {
    interval: string;
    interval_count: number;
  } | null;
  interval: string | null; // fallback interval property
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  metadata?: {
    days?: string;
  };
  prices: Price[];
}

const Pricing = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  useEffect(() => {
    // Add a small delay to ensure localStorage is ready
    const checkGuestStatus = () => {
      // Check if user is a guest
      const guestStatus = isGuestUser();
      console.log("🔍 Guest status check:", { guestStatus });

      setIsGuest(guestStatus);

      if (guestStatus) {
        const guestData = getGuestExamUser();
        console.log("🔍 Guest data:", guestData);
        setGuestUserId(guestData?.user_id || null);
      } else {
        console.log("🔍 Not a guest user, checking localStorage:");
        console.log("🔍 guestExamUser:", localStorage.getItem("guestExamUser"));
        console.log("🔍 access_token:", localStorage.getItem("access_token"));
      }
    };

    // Check immediately
    checkGuestStatus();

    // Also check after a small delay in case of timing issues
    setTimeout(checkGuestStatus, 100);

    async function fetchPlans() {
      setLoading(true);
      try {
        const res = await getSubscriptionPlans();
        const sortedProducts = res.products.sort((a, b) => {
          const priceA = a.prices[0]?.unit_amount || 0;
          const priceB = b.prices[0]?.unit_amount || 0;
          return priceA - priceB;
        });
        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchPlans();
  }, []);

  async function handleCheckout(priceId: string) {
    if (loadingPriceId) return;
    
    // 🚀 Safari Fix: Open window immediately on user click
    const newWindow = window.open("", "_blank");
    
    setLoadingPriceId(priceId);
    try {
      console.log("🚀 Creating checkout session for price:", priceId);
      console.log("👤 Guest user status:", { isGuest, guestUserId });
      console.log("🔍 Full localStorage state:");
      console.log("  - guestExamUser:", localStorage.getItem("guestExamUser"));
      console.log("  - access_token:", localStorage.getItem("access_token"));
      console.log("  - session_id:", localStorage.getItem("session_id"));

      if (isGuest && !guestUserId) {
        console.error("❌ Guest user but no guest user ID found!");
        alert("Error: Guest user ID not found. Please refresh the page and try again.");
        setLoadingPriceId(null);
        if (newWindow) newWindow.close(); // Close the blank window
        return;
      }

      console.log("📤 Calling createCheckoutSession with:", {
        priceId,
        isGuest,
        guestUserId: guestUserId || undefined
      });

      const res = await createCheckoutSession(
        priceId,
        `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        `${window.location.origin}/payment-canceled`,
        isGuest,
        guestUserId || undefined
      );

      console.log("✅ Checkout session created, redirecting to:", res.checkout_url);
      
      // 🚀 Safari Fix: Update the already opened window location
      if (newWindow) {
        newWindow.location.href = res.checkout_url;
      } else {
        // Fallback if window.open failed (popup blocker?)
        window.location.href = res.checkout_url;
      }
      
      // Note: We don't reset loadingPriceId here because the user is being redirected.
      // If they come back, the page will likely reload or they can refresh.
      // But just in case they cancel the tab open and stay here:
      setTimeout(() => setLoadingPriceId(null), 3000);
    } catch (error) {
      console.error("❌ Checkout session creation failed:", error);
      alert("Failed to create checkout session. Please try again.");
      setLoadingPriceId(null);
      if (newWindow) newWindow.close(); // Close the blank window
    }
  }
  const getFeatures = (planName: string) => {
    const features = {
      Light: [
        { text: "Unlimited full exam simulations", Icon: StarIcon },
        { text: "Detailed CEFR scoring + skill radar", Icon: CheckIcon },
        { text: "Personalized feedback & tips", Icon: ArrowIcon },
        { text: "Improved answers based on goal", Icon: InfoIcon },
      ],
      Pro: [
        { text: "Unlimited full exam simulations", Icon: StarIcon },
        { text: "Detailed CEFR scoring + skill radar", Icon: CheckIcon },
        { text: "Personalized feedback & tips", Icon: ArrowIcon },
        { text: "Improved answers based on goal", Icon: InfoIcon },
      ],
      Mastery: [
        { text: "Unlimited full exam simulations", Icon: StarIcon },
        { text: "Detailed CEFR scoring + skill radar", Icon: CheckIcon },
        { text: "Personalized feedback & tips", Icon: ArrowIcon },
        { text: "Improved answers based on goal", Icon: InfoIcon },
      ],
    };
    return features[planName as keyof typeof features] || [];
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center max-w-[1120px] w-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground mt-4" style={{ fontSize: "18px" }}>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-section">
      <div className="pricing-container">
        <div className="pricing-header">
  <h1 className="pricing-title">Choose Your Plan</h1>

  <p className="pricing-subtitle">
    All plans unlock the same speaking exam simulation and feedback – just choose how long you want access.
  </p>

  {isGuest && (
    <div className="guest-banner">
      <p>
        Please subscribe to unlock your full exam results and access all premium features.
      </p>
    </div>
  )}
</div>


        <div className="pricing-grid">
          {products.map((product) => {
            const price = product.prices[0];
            const isPopular = product.name === "Pro";
            const features = getFeatures(product.name);
            const interval = price.recurring?.interval || price.interval || "month";

            
            return (
              <div
                key={product.id}
                className={`pricing-card ${isPopular ? "popular" : ""}`}
              >
                {isPopular && <span className="badge">Best Value</span>}
            
                <div className="pricing-card-header">
                  <div className="plan-name">{product.name}</div>
            
                  <div className="plan-tagline">
                    {product.name === "Light" && "5-day quick prep"}
                    {product.name === "Pro" && "Best for exam month"}
                    {product.name === "Mastery" && "Full month of practice"}
                  </div>
            
                  <div className="plan-price-box">
                    <div className="price-currency-container">
                      <span className="plan-price">
                        ${(price.unit_amount / 100).toFixed(2)}
                      </span>
                      <span className="plan-currency">
                        {price.currency.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <span className="plan-access" style={{ color: "#6f7787" }}>
                        {product.metadata?.days || (product.name === "Light" ? 5 : product.name === "Pro" ? 15 : product.name === "Mastery" ? 30 : 30)} days access
                      </span>
                    </div>
                  </div>
                </div>
            
                <hr
                  className="border-t-1 my-4"
                  style={{ borderColor: "#005fcc" }}
                />
            
                <div className="pricing-card-content">
                  <ul className="feature-list">
                    {features.map((featureObj, i) => {
                      const { text, Icon } = featureObj;
                      return (
                        <li key={i} className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#005FCC] fill-current" />
                          <span>{text}</span>
                        </li>
                      );
                    })}
                  </ul>
            
                  <button
                    className={`choose-btn ${isPopular ? "popular-btn" : ""} ${loadingPriceId === price.id ? "opacity-70 cursor-not-allowed" : ""}`}
                    onClick={() => handleCheckout(price.id)}
                    disabled={!!loadingPriceId}
                  >
                    {loadingPriceId === price.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      "Unlock Full Access"
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm font-semibold text-yellow-800">Debug Info:</p>
            <p className="text-sm text-yellow-700">Is Guest: {isGuest ? "Yes" : "No"}</p>
            <p className="text-sm text-yellow-700">Guest User ID: {guestUserId || "None"}</p>
            <button 
              onClick={() => {
                console.log("Manual test - isGuestUser():", isGuestUser());
                console.log("Manual test - getGuestExamUser():", getGuestExamUser());
                console.log("Manual test - localStorage:", localStorage.getItem("guestExamUser"));
              }}
              className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
            >
              Test Guest Detection
            </button>
          </div> */}

       {isGuest && (
  <div className="fixed bottom-0 left-0 w-full bg-yellow-100 text-yellow-800 text-center py-3 border-t border-yellow-300 shadow-md">
    <p className="text-sm md:text-base font-medium">
      Guest users can view their trial exam details and results in the Exam List after purchasing a subscription plan.
    </p>
  </div>
)}


      </div>
    </div>

  );
};

export default Pricing;
